/**
 * INPUT SANITIZATION UTILITIES - Gateway Protection
 *
 * Clean and sanitize data before sending to backend
 */

import {
  createBlockedCharacterRegex,
  FIELD_SECURITY_CONFIGS,
} from '../constants/fieldValidation.constants';
import type { SanitizationResult } from '../models/sanitization.model';
import type { TournamentUpsert } from '../models/tournament.model';
import type { SignUpProfile, UserProfile } from '../models/user.model';

// =============================================================================
// UTILITY FUNCTIONS - Internal sanitization engine
// =============================================================================

/**
 * Main sanitization engine with field-specific security configuration
 */
export const sanitizeInput = (
  value: string,
  fieldType: keyof typeof FIELD_SECURITY_CONFIGS = 'TEXT',
): SanitizationResult => {
  const originalValue = value;
  let cleanValue = value;
  const removedCharacters: string[] = [];
  const violatedPatterns: string[] = [];

  if (typeof value !== 'string') {
    return {
      cleanValue: '',
      originalValue: value,
      wasModified: true,
      removedCharacters: [],
      violatedPatterns: [],
    };
  }

  const config = FIELD_SECURITY_CONFIGS[fieldType];

  // Remove dangerous characters
  const blockedRegex = createBlockedCharacterRegex(config.blockedCharacters);
  const foundDangerousChars = originalValue.match(blockedRegex);

  if (foundDangerousChars) {
    removedCharacters.push(...foundDangerousChars);
    cleanValue = cleanValue.replace(blockedRegex, '');
  }

  // Remove malicious patterns
  config.patterns.forEach((pattern: RegExp) => {
    if (pattern.test(cleanValue)) {
      violatedPatterns.push(pattern.source);
      cleanValue = cleanValue.replace(pattern, '');
    }
  });

  // Basic formatting cleanup
  cleanValue = cleanValue.replace(/\s+/g, ' ').trim();

  // Enforce max length
  if (cleanValue.length > config.maxLength) {
    cleanValue = cleanValue.substring(0, config.maxLength);
  }

  // Handle allowed characters (for username/email)
  if ('allowedCharacters' in config && config.allowedCharacters) {
    const allowedRegex = new RegExp(
      `[^${config.allowedCharacters.join('')}]`,
      'g',
    );
    cleanValue = cleanValue.replace(allowedRegex, '');
  }

  return {
    cleanValue,
    originalValue,
    wasModified: cleanValue !== originalValue,
    removedCharacters: Array.from(new Set(removedCharacters)),
    violatedPatterns: Array.from(new Set(violatedPatterns)),
  };
};

/**
 * Comprehensive data sanitization for any object
 */
export const sanitizeData = <T>(
  data: any,
  fieldMappings: Record<string, keyof typeof FIELD_SECURITY_CONFIGS>,
  options: {
    required?: string[];
    validators?: Record<string, (value: any) => string | undefined>;
  } = {},
): T => {
  const sanitized = {} as T;

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      if (options.required?.includes(key)) {
        // Set a default empty value for required fields
        (sanitized as any)[key] = '';
      }
      return;
    }

    const fieldType = fieldMappings[key] || 'TEXT';

    // Special handling for different value types
    if (typeof value === 'string') {
      if (options.validators?.[key]) {
        const validationResult = options.validators[key](value);
        (sanitized as any)[key] = validationResult || '';
      } else {
        (sanitized as any)[key] = sanitizeInput(value, fieldType).cleanValue;
      }
    } else if (typeof value === 'boolean') {
      (sanitized as any)[key] = Boolean(value);
    } else if (typeof value === 'number') {
      (sanitized as any)[key] = Number(value);
    } else if (value instanceof Date) {
      (sanitized as any)[key] = value;
    } else {
      // For other types, try to convert to string and sanitize
      (sanitized as any)[key] = sanitizeInput(
        String(value),
        fieldType,
      ).cleanValue;
    }
  });

  return sanitized;
};

// =============================================================================
// UTILITY FUNCTIONS - Common validators
// =============================================================================

/**
 * Simple validators for common field types
 */
export const validators = {
  date: (dateString: string): string => {
    if (typeof dateString !== 'string') return '';

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    return dateString;
  },

  countryCode: (code: string): string => {
    if (typeof code !== 'string') return '';

    const cleaned = code.toUpperCase().slice(0, 2);
    return /^[A-Z]{2}$/.test(cleaned) ? cleaned : '';
  },

  gender: (gender: string): string => {
    if (typeof gender !== 'string') return '';

    const validGenders = ['M', 'F', 'MALE', 'FEMALE'];
    const normalizedGender = gender.toUpperCase();
    return validGenders.includes(normalizedGender) ? normalizedGender : '';
  },
};

// =============================================================================
// CORE FUNCTIONS - Main external API (what you actually call)
// =============================================================================

/**
 * Pre-configured sanitization for user signup data
 */
export const sanitizeSignUpProfile = (profile: any): SignUpProfile => {
  return sanitizeData<SignUpProfile>(
    profile,
    {
      firstName: 'NAME',
      lastName: 'NAME',
      email: 'EMAIL',
      dateOfBirth: 'TEXT',
      nationality: 'TEXT',
      gender: 'TEXT',
    },
    {
      validators: {
        email: (email) => email?.toLowerCase(),
        dateOfBirth: validators.date,
        nationality: validators.countryCode,
        gender: validators.gender,
      },
    },
  );
};

/**
 * Pre-configured sanitization for tournament data
 */
export const sanitizeTournamentUpsert = (
  tournament: any,
): Partial<TournamentUpsert> => {
  return sanitizeData<Partial<TournamentUpsert>>(
    tournament,
    {
      name: 'TEXT',
      description: 'DESCRIPTION',
      password: 'TOURNAMENT_PASSWORD',
    },
    {
      validators: {
        private: (value) => (Boolean(value) ? 'true' : 'false'),
        numberOfSeasons: (value) => String(Number(value)),
        startDate: (value) => new Date(value).toISOString(),
      },
    },
  );
};

/**
 * Pre-configured sanitization for user profile updates
 * Preserves undefined values for partial updates (don't update those fields)
 */
export const sanitizeUserProfileUpdate = (
  profile: any,
): Partial<UserProfile> => {
  const sanitized: Partial<UserProfile> = {};

  // Only process fields that are actually provided (not undefined/null)
  Object.entries(profile).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      // For partial updates, preserve undefined (means "don't update this field")
      return;
    }

    switch (key) {
      case 'firstName':
      case 'lastName':
        (sanitized as any)[key] = sanitizeInput(
          value as string,
          'NAME',
        ).cleanValue;
        break;
      case 'email':
        (sanitized as any)[key] = sanitizeInput(
          (value as string)?.toLowerCase(),
          'EMAIL',
        ).cleanValue;
        break;
      case 'username':
        (sanitized as any)[key] = sanitizeInput(
          value as string,
          'USERNAME',
        ).cleanValue;
        break;
      case 'dateOfBirth':
        const validatedDate = validators.date(value as string);
        if (validatedDate) {
          (sanitized as any)[key] = validatedDate;
        }
        break;
      case 'nationality':
        const validatedCountry = validators.countryCode(value as string);
        if (validatedCountry) {
          (sanitized as any)[key] = validatedCountry;
        }
        break;
      case 'gender':
        const validatedGender = validators.gender(value as string);
        if (validatedGender) {
          (sanitized as any)[key] = validatedGender;
        }
        break;
      default:
        // For other fields, just sanitize as text
        if (typeof value === 'string') {
          (sanitized as any)[key] = sanitizeInput(value, 'TEXT').cleanValue;
        } else {
          (sanitized as any)[key] = value;
        }
    }
  });

  return sanitized;
};
